import { create } from 'zustand';
import {
  feimaChatSuperAdminGetPersonalizationConfig,
  type PersonalizationConfigResult,
  type NavTabDTO,
} from '@/api/superAdminApi';
import { THEME_COLOR, APP_TITLE, APP_SLOGAN } from '@/config/appConfig';

const ERKANG_HOSTNAMES = [
  'localhost',
  'erkang.ai',
  'test.erkang.ai',
  'www.erkang.ai',
  'www.erkangai.com',
  'erkangai.com',
];
const getPersonalizationPlatform = (): string =>
  ERKANG_HOSTNAMES.includes(window.location.hostname) ? 'erkang' : 'lingshu';

interface PersonalizationStore {
  productName: string;
  productSlogan: string;
  themeColor: string;
  logoUrl: string;
  websiteNavTabs: NavTabDTO[];
  loaded: boolean;
  loadConfig: () => Promise<void>;
  updateConfig: (config: Partial<PersonalizationConfigResult>) => void;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g)
    .toString(16)
    .padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`;
}

// 将 RGB 混入白色，生成浅色背景（amount: 0~1，越大越浅）
function lightenToWhite(
  r: number,
  g: number,
  b: number,
  amount: number
): string {
  return rgbToHex(
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount
  );
}

// 将 RGB 混入黑色，生成深色
function darkenToBlack(
  r: number,
  g: number,
  b: number,
  amount: number
): string {
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

export function applyThemeColor(color: string) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return;
  const [r, g, b] = hexToRgb(color);
  const root = document.documentElement;

  root.style.setProperty('--theme-main', color);
  root.style.setProperty(
    '--theme-main-hover-deep',
    darkenToBlack(r, g, b, 0.15)
  );
  root.style.setProperty(
    '--theme-main-hover-light',
    lightenToWhite(r, g, b, 0.2)
  );
  // 背景色（极浅，用于选中态背景）
  root.style.setProperty('--theme-main-bg', `rgba(${r}, ${g}, ${b}, 0.08)`);
  root.style.setProperty(
    '--theme-main-bg-active',
    `rgba(${r}, ${g}, ${b}, 0.12)`
  );
  root.style.setProperty('--theme-main-10', `rgba(${r}, ${g}, ${b}, 0.1)`);
  root.style.setProperty('--theme-main-20', `rgba(${r}, ${g}, ${b}, 0.2)`);
  // 浅色变体（用于渐变、边框等）
  root.style.setProperty('--theme-main-light', lightenToWhite(r, g, b, 0.35));
  root.style.setProperty('--theme-main-lighter', lightenToWhite(r, g, b, 0.6));
  root.style.setProperty('--theme-main-dark', darkenToBlack(r, g, b, 0.1));
}

const resolveThemeColor = (color?: string): string =>
  /^#[0-9a-fA-F]{6}$/.test(color || '') ? (color as string) : THEME_COLOR;

const resolveEnabledWebsiteTabs = (tabs?: NavTabDTO[]): NavTabDTO[] =>
  (tabs || []).filter(t => t.enabled).sort((a, b) => a.order - b.order);

export const usePersonalizationStore = create<PersonalizationStore>(
  (set, get) => ({
    productName: APP_TITLE,
    productSlogan: APP_SLOGAN,
    themeColor: THEME_COLOR,
    logoUrl: '',
    websiteNavTabs: [],
    loaded: false,

    updateConfig: config => {
      const current = get();
      const color =
        config.themeColor === undefined
          ? current.themeColor
          : resolveThemeColor(config.themeColor);
      applyThemeColor(color);
      set({
        productName:
          config.productName === undefined
            ? current.productName
            : config.productName || APP_TITLE,
        productSlogan:
          config.productSlogan === undefined
            ? current.productSlogan
            : config.productSlogan || APP_SLOGAN,
        themeColor: color,
        logoUrl:
          config.logoUrl === undefined ? current.logoUrl : config.logoUrl || '',
        websiteNavTabs:
          config.websiteNavTabs === undefined
            ? current.websiteNavTabs
            : resolveEnabledWebsiteTabs(config.websiteNavTabs),
        loaded: true,
      });
    },

    loadConfig: async () => {
      if (get().loaded) return;
      try {
        const res = await feimaChatSuperAdminGetPersonalizationConfig(
          getPersonalizationPlatform(),
          { noMessage: true }
        );
        if (!res) return;
        const color = resolveThemeColor(res.themeColor);
        applyThemeColor(color);
        set({
          productName: res.productName || APP_TITLE,
          productSlogan: res.productSlogan || APP_SLOGAN,
          themeColor: color,
          logoUrl: res.logoUrl || '',
          websiteNavTabs: resolveEnabledWebsiteTabs(res.websiteNavTabs),
          loaded: true,
        });
      } catch {
        // 静默失败，保持默认值
      }
    },
  })
);
